import {Component, ElementRef, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {AConst} from '../../core/a-const.enum';
import {MediaHelperService} from '../../core/media-helper.service';
import {NotificationService} from '../../shared/notification.service';
import {AnnotationRendererService} from '../annotation-renderer.service';
import {AnnotationContainer} from '../annotation-container';
import {AnnotationMenusService} from '../annotation-menus.service';
import {Annotation} from '../annotation';

@Component({
  selector: 'app-annotation-canvas',
  templateUrl: './annotation-canvas.component.html',
  styleUrls: ['./annotation-canvas.component.scss']
})
export class AnnotationCanvasComponent implements OnInit, OnDestroy {

  @ViewChild('canvasElement', { static: true }) canvas: ElementRef;
  @Input() curAnn: Annotation;
  @Input() parentContainerId;
  @Input() dashboardId;
  @Input() lockHeight;
  @Input() edit;

  private container: AnnotationContainer;
  private mouseCoord = {x: 0, y: 0};
  private imageSize;
  private destroyed = false;

  constructor(private mediaHelper: MediaHelperService,
              private notificationService: NotificationService,
              private annotationRendererService: AnnotationRendererService,
              private annotationMenusService: AnnotationMenusService) {
  }

  ngOnInit() {
    const windowHeight = screen.height;
    const windowWidth = screen.width;

    if (windowHeight > windowWidth) {
      this.imageSize = windowHeight;
    } else {
      this.imageSize = windowWidth;
    }

    if (this.curAnn.aneSet) {
      this.initAnnotation();
    } else {
      this.curAnn.canvasCallback = () => {
        this.initAnnotation();
      };
    }
    this.canvas.nativeElement.addEventListener('mousedown', event => {
      this.setClickAction(event);
    });
    this.canvas.nativeElement.addEventListener('mousemove', event => {
      this.mouseMove(event);
    });
    this.canvas.nativeElement.addEventListener('mouseup', () => {
      this.mouseUp();
    });
    this.canvas.nativeElement.addEventListener('touchstart', event => {
      this.touchStart(event);
    });
    this.canvas.nativeElement.addEventListener('touchmove', event => {
      this.touchMove(event);
    });
    this.canvas.nativeElement.addEventListener('touchend', () => {
      this.touchEnd();
    });
  }

  ngOnDestroy(): void {
    this.curAnn.canvasCallback = null;
    this.canvas.nativeElement.removeEventListener('mousedown', event => {
      this.setClickAction(event);
    });
    this.canvas.nativeElement.removeEventListener('mousemove', event => {
      this.mouseMove(event);
    });
    this.canvas.nativeElement.removeEventListener('mouseup', () => {
      this.mouseUp();
    });
    this.canvas.nativeElement.removeEventListener('touchstart', event => {
      this.touchStart(event);
    });
    this.canvas.nativeElement.removeEventListener('touchmove', event => {
      this.touchMove(event);
    });
    this.canvas.nativeElement.removeEventListener('touchend', () => {
      this.touchEnd();
    });
    this.destroyed = true;
  }

  private initAnnotation() {
    this.curAnn.resetState();
    if (this.edit) {
      this.curAnn.setDrawState();
    }
    this.container = new AnnotationContainer();
    this.container.canvas = this.canvas.nativeElement;
    this.container.curAnn = this.curAnn;
    this.container.dashboardId = this.dashboardId;
    this.container.editable = this.edit;
    this.container.imageId = this.curAnn.annotateImage[AConst.IMAGE_ID];
    this.container.lockHeight = this.lockHeight;
    this.container.menus = this.annotationMenusService.getMenus(this.container);
    this.container.offsets = 2;
    this.container.parentContainerId = this.parentContainerId;
    this.container.quality = 70;

    this.curAnn.aneSet = false;

    this.draw();
  }

  private draw() {
    this.annotationRendererService.drawAnnotations(this.container, {mouseCoord: this.mouseCoord});
    this.annotationMenusService.drawMenus(this.container, this.mouseCoord);
    window.requestAnimationFrame(() => {
      if (!this.destroyed) {
        this.draw();
      }
    });
  }

  private mouseXY(event) {
    let touches, lastTouch;
    const mouseCoord = {x: 0, y: 0};
    const bodyRect = document.body.getBoundingClientRect();
    const canvasRect = this.canvas.nativeElement.getBoundingClientRect();
    const offsetY = canvasRect.top - bodyRect.top;
    const offsetX = canvasRect.left;
    lastTouch = event;
    touches = event.targetTouches;
    if (touches) {
      lastTouch = touches[touches.length - 1];
    }
    mouseCoord.x = lastTouch.pageX - offsetX;
    mouseCoord.y = lastTouch.pageY - offsetY;
    return mouseCoord;
  }

  private checkMove(event) {
    let ax, ay;
    const mouseCoord = this.mouseXY(event);
    ax = this.annotationRendererService.cx2ax(this.container, mouseCoord.x);
    ay = this.annotationRendererService.cy2ay(this.container, mouseCoord.y);
    if (this.curAnn.state.draw.drawing) {
      this.annotationRendererService.setAnnotationEnd(this.container, this.curAnn.selectedAnnPoint, mouseCoord);
    }
    if (this.curAnn.state.draw.moving) {
      const diffX = ax - this.curAnn.selectedAnnPoint.x1;
      const diffY = ay - this.curAnn.selectedAnnPoint.y1;
      this.curAnn.selectedAnnPoint.x1 = ax;
      this.curAnn.selectedAnnPoint.y1 = ay;
      this.curAnn.selectedAnnPoint.x2 += diffX;
      this.curAnn.selectedAnnPoint.y2 += diffY;
    }
    return mouseCoord;
  }


  private mouseMove(event) {
    this.mouseCoord = this.checkMove(event);
  }

  private mouseUp() {
    this.checkFinish();
  }

  private touchStart(event) {
    event.preventDefault();
    this.setClickAction(event);
  }

  private touchMove(event) {
    event.preventDefault();
    this.mouseCoord = this.checkMove(event);
  }

  private touchEnd() {
    this.checkFinish();
  }

  private newAnnotation(scope, start, fn) {
    const x = this.annotationRendererService.cx2ax(this.container, start.x);
    const y = this.annotationRendererService.cy2ay(this.container, start.y);
    this.curAnn.createAnnotation(scope, x, y, item => {
      if (item) {
        this.curAnn.selectAnnotation(item);
        this.curAnn.state.draw.drawing = true;
      }
      if (fn) {
        fn(item);
      }
    });
  }

  private setAction(container: AnnotationContainer, mouseCoord) {
    let res = {foundAction: false};
    const state = container.curAnn.state;
    const hoverMenIdx = this.annotationMenusService.hoverMenuIndex(container, mouseCoord);
    if (hoverMenIdx >= 0) {
      const action = this.annotationMenusService.getMenus(container)[hoverMenIdx].actionState;
      container.curAnn.toggleStateAction(action);
      res.foundAction = true;
    } else if (state.action === container.curAnn.actionStates.draw && container.curAnn.selectedAnnPoint) {
      res = this.annotationRendererService.setDrawAction(container, mouseCoord);
    }
    return res;
  }


  private setClickAction(event) {
    let action, hoverAnn, foundAction;
    const m = this.mouseXY(event);

    this.checkFinish();
    // First check actions to current selected annotation
    action = this.setAction(this.container, m);
    foundAction = action.foundAction;
    if (!foundAction) {
      // Next check actions to the annotation close to
      // mouse
      hoverAnn = this.annotationRendererService.getHoverAnnotation(this.container, m);
      if (hoverAnn) {
        // foundAction must be set in order to prevent
        // creation of new annotation
        foundAction = true;
        this.curAnn.selectAnnotation(hoverAnn);
      }
    }
    if (!foundAction &&
      this.curAnn.state.action === this.curAnn.actionStates.draw) {
      this.newAnnotation(this, m, null);
    }
  }

  private checkFinish() {
    if (this.curAnn.state.draw.drawing) {
      this.curAnn.state.draw.drawing = false;
      this.annotationRendererService.finishAnnotation(this.container, this.curAnn.selectedAnnPoint);
    }
    if (this.curAnn.state.draw.moving) {
      this.curAnn.state.draw.moving = false;
      this.annotationRendererService.finishAnnotation(this.container, this.curAnn.selectedAnnPoint);
    }
  }

}
